import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { SkillTree } from '@/components/SkillTree';
import { ScoreRadar } from '@/components/WindCharts';
import { SkillTrends } from '@/components/SkillTrends';
import { MyGearCard } from '@/components/MyGearCard';
import { PreferencesCard } from '@/components/PreferencesCard';
import { StravaCard } from '@/components/StravaCard';
import { GoalsStreakCard } from '@/components/GoalsStreakCard';
import { WindAlertsWidget } from '@/components/WindAlertsWidget';
import { UpgradeNudge } from '@/components/UpgradeNudge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Activity, Trophy, GitBranch, MapPin, Flame, Award, Video, Loader2, ArrowRight, Sparkles, Calendar,
} from 'lucide-react';

const ICONS = { video: Video, 'map-pin': MapPin, flame: Flame, award: Award };

const StatCard = ({ icon: Icon, label, value, accent = '#00B4D8' }) => (
  <div className="kite-card p-4">
    <div className="flex items-center gap-2 text-muted-foreground"><Icon className="w-4 h-4" style={{ color: accent }} /><span className="label-eyebrow">{label}</span></div>
    <div className="font-data text-3xl font-semibold mt-2" style={{ color: accent }}>{value ?? '—'}</div>
  </div>
);

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [prog, setProg] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get('/users/me/progress'), api.get('/analyses')])
      .then(([p, a]) => { setProg(p.data); setRecent(a.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Handle the Strava OAuth redirect result.
  useEffect(() => {
    const strava = searchParams.get('strava');
    if (!strava) return;
    if (strava === 'success') { toast.success(t('dashboard.stravaConnected')); refreshUser(); }
    else if (strava === 'error') { toast.error(t('dashboard.stravaError')); }
    searchParams.delete('strava');
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#00B4D8] animate-spin" /></div>;

  const stats = prog?.stats || {};
  const hasData = (stats.total_analyses || 0) > 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow text-[#48CAE4] mb-2">{t('dashboard.eyebrow')}</div>
          <h1 className="font-display text-4xl sm:text-5xl">{t('dashboard.greeting', { name: user?.name })}</h1>
          <p className="text-muted-foreground mt-1">{t('dashboard.detectedLevel')} <span className="text-white capitalize">{stats.detected_level || user?.skill_level}</span></p>
        </div>
        <Button onClick={() => navigate('/analyze')} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="dashboard-analyze-btn">
          <Sparkles className="w-4 h-4 mr-2" /> {t('dashboard.newAnalysis')}
        </Button>
      </div>

      <UpgradeNudge />

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <MyGearCard />
        <PreferencesCard />
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <GoalsStreakCard />
        <StravaCard />
      </div>

      <div className="mt-6">
        <WindAlertsWidget />
      </div>

      {!hasData ? (
        <div className="kite-card p-12 text-center mt-10">
          <div className="w-16 h-16 rounded-2xl bg-[#00B4D8]/12 flex items-center justify-center mx-auto"><Video className="w-8 h-8 text-[#00B4D8]" /></div>
          <h2 className="font-display text-3xl mt-5">{t('dashboard.emptyTitle')}</h2>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto">{t('dashboard.emptyDesc')}</p>
          <Button onClick={() => navigate('/analyze')} className="mt-6 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl">{t('dashboard.analyzeFirst')} <ArrowRight className="w-4 h-4 ml-1" /></Button>
        </div>
      ) : (
        <>
          {/* stats */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-8">
            <StatCard icon={Activity} label={t('dashboard.statAnalyses')} value={stats.total_analyses} />
            <StatCard icon={Trophy} label={t('dashboard.statAvg')} value={stats.avg_overall} accent="#48CAE4" />
            <StatCard icon={Award} label={t('dashboard.statBest')} value={stats.personal_best} accent="#2EE59D" />
            <StatCard icon={GitBranch} label={t('dashboard.statSkills')} value={`${stats.skills_unlocked}/${stats.skills_total}`} accent="#FF6B35" />
            <StatCard icon={MapPin} label={t('dashboard.statSpots')} value={stats.spots_visited} accent="#FFB020" />
          </div>

          {/* badges */}
          {prog?.badges?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-5">
              {prog.badges.map((b) => { const I = ICONS[b.icon] || Award; return (
                <span key={b.key} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-[#0E1114] border border-white/10"><I className="w-4 h-4 text-[#FFB020]" /> {b.label}</span>
              ); })}
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-6 mt-8">
            <div className="lg:col-span-2">
              <SkillTrends history={prog.score_history} />
            </div>
            <div className="kite-card p-5">
              <h3 className="font-semibold mb-2">{t('dashboard.latestScores')}</h3>
              <ScoreRadar scores={prog.latest_scores} />
            </div>
          </div>

          {/* skill tree */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-3xl">{t('dashboard.skillTree')}</h2>
              <span className="text-sm text-muted-foreground font-data">{t('dashboard.unlocked', { unlocked: stats.skills_unlocked, total: stats.skills_total })}</span>
            </div>
            <SkillTree nodes={prog.skill_tree} />
          </div>

          {/* recent sessions */}
          <div className="mt-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-3xl">{t('dashboard.recentSessions')}</h2>
              <Link to="/sessions" className="text-sm text-[#48CAE4] hover:text-[#00B4D8] flex items-center gap-1">{t('dashboard.allSessions')} <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {recent.slice(0, 6).map((s) => {
                const sc = s.result?.scores; const ov = sc ? (Object.values(sc).reduce((p,c)=>p+c,0)/Object.values(sc).length).toFixed(1) : null;
                return (
                  <Link key={s.id} to={`/analysis/${s.id}`} className="kite-card overflow-hidden group" data-testid={`session-card-${s.id}`}>
                    <div className="relative h-32">
                      {s.thumbnail ? <img src={s.thumbnail} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#0A0A0A] flex items-center justify-center"><Video className="w-7 h-7 text-muted-foreground" /></div>}
                      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,10,10,0) 40%, rgba(10,10,10,0.9))' }} />
                      {ov && <span className="absolute bottom-2 right-2 font-data text-sm px-2 py-0.5 rounded-md bg-black/60 text-[#00B4D8]">{ov}/10</span>}
                      {s.status === 'processing' && <span className="absolute top-2 left-2 text-xs px-2 py-0.5 rounded-full bg-black/60 text-[#48CAE4] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> {t('dashboard.analyzing')}</span>}
                    </div>
                    <div className="p-3">
                      <div className="font-medium capitalize truncate">{s.result?.skill_level_detected || s.status} {t('dashboard.sessionWord')}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
                        <Calendar className="w-3 h-3" /> {(s.created_at || '').slice(0, 10)}
                        {s.meta?.spot_name && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.meta.spot_name}</span>}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
